import {
  Button,
  Divider,
  DropdownButton,
  DropdownButtonContext,
  EmptyState,
  FlexBox,
  Icon,
  InputText,
  Spinner,
  Text,
  SimplePagination,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import moment from 'moment';
import { useContext, useMemo, useState } from 'react';

import { aiAgentsApi } from 'apis/aiAgents';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

import { useMimSidekickChat } from '../../mimSidekickChat.context';

import styles from './mimSidekickHistory.module.scss';

const PAGE_SIZE = 8;

/**
 * TODO: Remove when BE supports it
 */
const IS_SEARCH_ENABLED = false;

export const MimSidekickHistoryPanel = (): JSX.Element => {
  const { chatId, startNewChat, selectChat } = useMimSidekickChat();
  const { closeDropdown } = useContext(DropdownButtonContext);
  const [query, setQuery] = useState('');

  const {
    items: chats,
    total,
    arg,
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
  } = usePaginatedEndpoint({
    endpoint: aiAgentsApi.endpoints.readAiAgentsSidekickConversations,
    initialArg: {
      limit: PAGE_SIZE,
      offset: 0,
    },
    itemsSelector: (response) => response.data.conversations,
    totalSelector: (response) => response.meta.total,
    infinite: false,
  });

  const offset = Number(arg?.offset ?? 0);
  const isLoading =
    asyncStatus === 'pending' || asyncStatus === 'uninitialized';

  const isSearching = IS_SEARCH_ENABLED && query.trim().length > 0;
  const visible = useMemo(() => {
    if (isSearching) {
      const term = query.trim().toLowerCase();
      if (!term) return chats;

      return chats.filter((chat) => !!chat.title?.toLowerCase().includes(term));
    }

    return chats;
  }, [chats, query, isSearching]);

  return (
    <FlexBox flexDirection="column" gap="200" className={styles.panel}>
      <Button
        variant="secondary"
        size="large"
        onClick={() => {
          startNewChat();
          closeDropdown();
        }}
      >
        New chat
      </Button>

      {IS_SEARCH_ENABLED && (
        <InputText
          icon="search"
          type="search"
          placeholder="Search chats..."
          value={query}
          aria-label="Search chats"
          onChange={(event) => setQuery(event.target.value)}
        />
      )}

      <FlexBox
        justifyContent="space-between"
        alignItems="center"
        padding="0 200"
      >
        <Text.Paragraph as="span" variant="3">
          {isSearching ? 'Search results' : 'Recent chats'}
        </Text.Paragraph>
        <Text.Paragraph as="span" variant="4" subdued>
          {isSearching ? `${visible.length} matches` : 'Latest first'}
        </Text.Paragraph>
      </FlexBox>

      <FlexBox flexDirection="column" gap="0" className={styles.list}>
        {isLoading && (
          <FlexBox justifyContent="center" alignItems="center" flex="1">
            <Spinner />
          </FlexBox>
        )}

        {!isLoading && visible.length === 0 && (
          <FlexBox justifyContent="center" alignItems="center" flex="1">
            <EmptyState
              icon={null}
              title={isSearching ? 'No chats found' : 'No chats yet'}
              description={
                isSearching
                  ? 'Try a different search.'
                  : 'Ask Mim something to start one.'
              }
            />
          </FlexBox>
        )}

        {!isLoading &&
          visible.map((chat) => {
            const isCurrent = chat.id === chatId;

            return (
              <FlexBox
                key={chat.id}
                as="button"
                gap="200"
                alignItems="center"
                className={classnames(styles.row, {
                  [styles.rowCurrent]: isCurrent,
                })}
                onClick={() => {
                  selectChat(chat.id);
                  closeDropdown();
                }}
              >
                <Icon name="chat" size={16} className={styles.rowIcon} />
                <FlexBox flexDirection="column" className={styles.rowText}>
                  <Text.Paragraph
                    as="span"
                    variant="3"
                    className={styles.rowTitle}
                  >
                    {chat.title ?? 'Untitled chat'}
                  </Text.Paragraph>
                  <Text.Paragraph as="span" variant="4" subdued>
                    {moment(chat.updatedAt).fromNow()}
                    {isCurrent ? ' · Current' : ''}
                  </Text.Paragraph>
                </FlexBox>
              </FlexBox>
            );
          })}
      </FlexBox>

      <Divider />

      <FlexBox
        justifyContent="space-between"
        alignItems="center"
        padding="0 200"
      >
        <Text.Paragraph as="span" variant="4" subdued>
          {!total
            ? '0 of 0'
            : `${offset + 1}–${offset + chats.length} of ${total}`}
        </Text.Paragraph>
        <SimplePagination
          previousButtonText="Previous"
          nextButtonText="Next"
          previousButtonDisabled={!hasPreviousPage}
          nextButtonDisabled={!hasNextPage}
          previousButtonClick={previousPage}
          nextButtonClick={nextPage}
        />
      </FlexBox>
    </FlexBox>
  );
};

export const MimSidekickHistory = (): JSX.Element => (
  <Tooltip content="Chat history">
    <DropdownButton
      icon="history"
      noArrow
      fixed
      destroyOnClose
      buttonProps={{ variant: 'secondary' }}
      // The floating panel drags by its header, which this dropdown sits inside.
      onPointerDown={(event) => event.stopPropagation()}
      dropdown={<MimSidekickHistoryPanel />}
    />
  </Tooltip>
);
